import EventEmitter from "events"
import _ from "lodash"

const IMAGE_REGEX = /\.(jpeg|jpg|png|gif|webp|bmp|svg|tiff|heic)$/i
const TEXT_REGEX = /\.(txt|py|cpp|c|h|js|jsx|ts|tsx|conf|json|md|csv|css|scss|html|htm)$/i
const VIDEO_REGEX = /\.(mp4|mov|avi|mkv|webm|flv|wmv|m4v)$/i
const AUDIO_REGEX = /\.(mp3|wav|flac|aac|ogg|wma|m4a|opus)$/i

export enum TFileTypes {
    file = 0,
    dir = 1,
    partial = 2
}
export interface IDir {
    _id:string
    parentId:string|null
    name:string
}
export interface IFile extends IDir {
    size:number
    created:number
    updated:number
    createdBy:string
    updatedBy:string
    type:TFileTypes
    read:string[]|null
    write:string[]|null
    delete:string[]|null
    
    // partial
    uploaded?:number
    serverId?:string
}

export class Dir extends EventEmitter implements IDir {
    private __id:string
    private _parentId:string|null
    private _name:string
    
    static get ROOT_ID() { return "0" }

    constructor(file:IDir){
        super()
        this.__id = file._id
        this._parentId = file.parentId
        this._name = file.name
    }
    get _id(){return this.__id}
    get parentId(){return this._parentId}
    get name(){return this._name}

    update(file:FMFile){
        this._parentId = file.parentId
        this._name = file.name
        return this
    }
    static serialize(dir:Dir){
        return `${dir._id}:${dir.parentId||""}:${dir.name}`
    }
}

export interface IFilePath {
    _id:string
    name:string
    path:string
    parent:IFilePath|null
    setPath:(path:string) => string
}

export function ComputePath(file:IFilePath) : string {
    if (file.path || file._id==Dir.ROOT_ID) return file.path
    else return file.setPath( (file.parent ? ComputePath(file.parent) : "") + "/" + file.name )
}

export class FMFile implements IFile {
    protected _json:IFile
    private _parent:FMFile|null = null
    private _children:FMFile[]|null = null
    private _childIds:string[]|null = null
    private _path:string = ""

    constructor(file:IFile){
        this._json = file
        if (FMFile.isDir(file)) this._children = []
    }

    get _id(){return this._json._id}
    get parentId(){return this._json.parentId}
    get parent(){return this._parent}
    get name(){return this._json.name}
    get size(){return this._json.size}
    get type(){return this._json.type}
    get created(){return this._json.created}
    get updated(){return this._json.updated}
    get createdBy(){return this._json.createdBy}
    get updatedBy(){return this._json.updatedBy}
    get read(){return this._json.read}
    get write(){return this._json.write}
    get delete(){return this._json.delete}
    get uploaded(){return this._json.uploaded}
    get serverId(){return this._json.serverId}

    get path(){return this._path}
    get children(){return this._children}
    get childIds(){return this._childIds}
    get isRoot(){return this._id==Dir.ROOT_ID}
    get isHome(){return this.parentId==Dir.ROOT_ID && this.name=="Home"}

    get extension(){
        const index = this.name.lastIndexOf(".")
        return index>=0 ? this.name.substring(index+1) : ""
    }
    get dotExtension(){
        const ext = this.extension
        return ext ? `.${ext}` : ""
    }

    setChildIds(ids:string[]) {
        this._childIds = ids
        return this
    }
    setPath(path:string){
        this._path = path
        return path
    }

    // update file uploaded
    /**
     * 
     * @param size 
     * @returns true if file is fully updated
     */
    append(size:number){
        if (this._json.uploaded===undefined) throw {status:400,msg:"File is not partial"}
        else if (this._json.uploaded+size>this.size) throw {status:400,msg:"Buffer exceeds file size"}

        this._json.uploaded += size
        this._json.updated = Date.now()
        if (this._json.uploaded==this.size) {
            delete this._json.uploaded
            delete this._json.serverId
            this._json.type = TFileTypes.file
            this._json.uploaded = Date.now()
            return true
        }
        else return false
    }

    // update computed values
    updateFrom(old:FMFile){
        this._children = old._children
        this._parent = old._parent
        this._path = old._path
        return this
    }

    getChildByName(name:string){ return this._children?.find(f => f.name==name) }
    removeChild(file:FMFile){
        if (this._childIds) _.pull(this._childIds, file._id)
        if (!this._children) return

        const index = this._children.findIndex(f => f._id==file._id)
        if (index>=0) this._children.splice(index,1)
    }
    has(name:string){ return !!this.getChildByName(name)}
    toJSON(){ return this._json }
    static serialize(file:FMFile, userId:string){
        let ret = [file._id,file.parentId||"",file.name,file.type,file.size,file.created,file.updated,file.createdBy,file.updatedBy,
            file.children ? file.children.filter(file => FMFile.readCheck(file, userId)).map(ch => ch._id).join(",") : "", 
            JSON.stringify(file.read), JSON.stringify(file.write), JSON.stringify(file.delete)
        ].join(":")
        if (FMFile.isPartial(file)) ret += `:${file.uploaded}:${file.serverId}`
        return ret
    }
    static deserialize(file:string) : FMFile {
        const [_id,parentId,name,type,size,created,updated,createdBy,updatedBy,childIds,read,write,_delete,uploaded,serverId] = file.split(":")
        const ret = new FMFile({
            _id,parentId,name,type: type=="1" ? TFileTypes.dir : TFileTypes.file,
            size:parseInt(size),created:parseInt(created),updated:parseInt(updated),
            createdBy,updatedBy,read:JSON.parse(read),write:JSON.parse(write),delete:JSON.parse(_delete),
            uploaded:parseInt(uploaded), serverId
        })
        return childIds.length ? ret.setChildIds(childIds.split(",")) : ret
    }

    static isFile(file:IFile) { return file.type==TFileTypes.file }
    static isDir(file:IFile) { return file.type==TFileTypes.dir }
    static isPartial(file:IFile) { return file.type==TFileTypes.partial }
    static isImage(file:IFile) { return IMAGE_REGEX.test(file.name) }
    static isText(file:IFile) { return TEXT_REGEX.test(file.name) }   
    static isVideo(file:IFile) { return VIDEO_REGEX.test(file.name) } 
    static isAudio(file:IFile) { return AUDIO_REGEX.test(file.name) }
    static canPreview(file:IFile) { return this.isText(file) || this.isImage(file) || this.isVideo(file) || this.isAudio(file) }
    
    static getFileExt(name:string){
        const match = name.match(/^(.+?)\.([^\.]+)$/)
        if (match) return [match[1], match[2]]
        else return [name, ""]
    }
    static createRoot(){
        return new FMFile({
            _id:Dir.ROOT_ID, parentId:null, name:"", size:0, created:0, updated:0, createdBy:"", updatedBy:"",
            type:TFileTypes.dir, read:["admin"], write:["admin"], delete:["admin"]
        })
    }
    
    static validate(name:string) { return /^[^\/]+$/.test(name) }
    clearParent(){
        this._json.parentId = null
        return this
    }

    setParent(parent:FMFile|null) {
        this._parent = parent
        return this
    }

    computePath(clear?:boolean){ 
        if (clear) this._path = ""
        ComputePath(this) 
        return this
    }

    addChild(file:FMFile){
        if (!this._children) throw "Only directory can have a child"
        else this._children.push(file)
        return this
    }

    /**
     * 
     * @param file 
     * @param userId 
     * @param isDirect if recursive, users don't have access to system folders
     * @returns 
     */
    static readCheck(file:FMFile, userId:string, isDirect:boolean=true) : boolean {
        if (file.isRoot || file.isHome) return isDirect ? true : false
        else if (file.createdBy==userId) return true
        else return !!file.read?.includes(userId) || this.readCheck(file.parent!, userId, false)
    }

    static writeCheck(file:FMFile, userId:string) : boolean {
        if (file.createdBy==userId) return true
        else if (!file.parent) return false
        else return !!file.write?.includes(userId) || this.writeCheck(file.parent, userId)
    }

    static deleteCheck(file:FMFile, userId:string) : boolean{
        if (file.createdBy==userId) return true
        else if (!file.parent) return false
        else return !!file.delete?.includes(userId) || this.deleteCheck(file.parent, userId)
    }    
}
